import { Category } from '@/interfaces/catalog/Category'

export interface FlattenedCategory {
  id: string
  name: string
  level: number
}

/**
 * Flattens a hierarchical category tree into a flat array with indentation indicators.
 * @param categories - Array of categories to flatten
 * @param excludeId - Optional category ID to exclude from the result (useful when editing a category to prevent circular references)
 * @param level - Current nesting level (used for recursion)
 * @returns Array of flattened categories with indentation prefixes
 */
export const flattenCategories = (
  categories: Category[],
  excludeId?: string,
  level = 0,
): FlattenedCategory[] => {
  const result: FlattenedCategory[] = []

  for (const category of categories) {
    if (category.id === excludeId) {
      continue
    }

    result.push({
      id: category.id,
      // API posílá i `materializedName` (cesta "Rodič > Dítě"), schéma ho zatím nemá
      name: (category as Category & { materializedName?: string }).materializedName ?? category.name,
      level,
    })

    if (category.children && category.children.length > 0) {
      const children = flattenCategories(category.children, excludeId, level + 1)
      result.push(...children)
    }
  }

  return result
}

/**
 * Finds a category by ID in a hierarchical category tree.
 * @param categories - Array of categories to search
 * @param id - Category ID to find
 * @returns The category if found, null otherwise
 */
export const findCategoryById = (
  categories: Category[],
  id: string,
): Category | null => {
  for (const category of categories) {
    if (category.id === id) {
      return category
    }
    if (category.children) {
      const found = findCategoryById(category.children, id)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Gets all ancestor IDs of a category by traversing up the tree via parentId.
 * @param categories - Array of categories to search
 * @param categoryId - Category ID to find ancestors for
 * @returns Array of ancestor category IDs (from root to direct parent)
 */
export const getAncestors = (
  categories: Category[],
  categoryId: string,
): string[] => {
  const category = findCategoryById(categories, categoryId)
  if (!category || !category.parentId) {
    return []
  }

  const ancestors = getAncestors(categories, category.parentId)
  return [...ancestors, category.parentId]
}

/**
 * Gets all descendant IDs of a category by recursively traversing children.
 * @param category - Category to get descendants for
 * @returns Array of descendant category IDs
 */
export const getDescendants = (category: Category): string[] => {
  const descendants: string[] = []

  if (category.children && category.children.length > 0) {
    for (const child of category.children) {
      descendants.push(child.id)
      descendants.push(...getDescendants(child))
    }
  }

  return descendants
}

/**
 * Gets all related category IDs (ancestors + self + descendants) for a given category.
 * @param categories - Array of categories to search
 * @param categoryId - Category ID to get related IDs for
 * @returns Array of related category IDs
 */
export const getRelatedCategoryIds = (
  categories: Category[],
  categoryId: string,
): string[] => {
  const category = findCategoryById(categories, categoryId)
  if (!category) {
    return []
  }

  const ancestors = getAncestors(categories, categoryId)
  const descendants = getDescendants(category)

  return [...ancestors, categoryId, ...descendants]
}

